/**
 * Region Configuration
 * 
 * This module provides region-specific values based on the VITE_REGION environment variable.
 * Values are determined at build time for each Netlify deployment.
 * 
 * US (storily.io): VITE_REGION=US or undefined
 * UK (storymarketing.uk): VITE_REGION=UK
 */

type Region = 'US' | 'UK';

const region: Region = (import.meta.env.VITE_REGION as Region) || 'US';

export const isUK = region === 'UK';
export const isUS = region === 'US';

export const regionConfig = {
  region,
  
  // Currency
  currency: isUK ? '£' : '$',
  
  // Contact Information
  phone: isUK ? '01789 334201' : '(888) 505-6258',
  phoneLink: isUK ? 'tel:+441789334201' : 'tel:+18885056258',
  email: 'hello@storymarketing.group', // Same for both regions
  
  // Address
  address: isUK 
    ? 'The Kiln, 2 Copenhagen St, Worcester WR1 2HB' 
    : '800 Town & Country Blvd Suite 500, Houston, TX 77024',
  countryRegion: isUK ? 'Worcester, United Kingdom' : 'Texas, United States',
  mapsUrl: isUK 
    ? 'https://www.google.com/maps/place/The+Kiln,+2+Copenhagen+St,+Worcester+WR1+2HB,+UK'
    : 'https://www.google.com/maps/place/800+Town+and+Country+Blvd+Suite+500,+Houston,+TX+77024,+USA',
  
  // Branding
  siteName: isUK ? 'Story Marketing' : 'Storily',
  baseUrl: isUK ? 'https://storymarketing.uk' : 'https://storily.io',
  
  // Legal/Compliance (UK-specific)
  icoRegistration: isUK ? 'ZC060558' : null,
  dpo: isUK ? {
    name: 'Kyle Jones',
    email: 'kyle@storymarketing.group',
    address: 'The Kiln, 2 Copenhagen St, Worcester WR1 2HB'
  } : null,
  governingLaw: isUK ? 'England and Wales' : 'United States',
  lawfulBases: isUK ? ['consent', 'contract', 'legitimate interests'] : [],
  
  // Pricing (same values, different currency display)
  pricing: {
    minimums: 197,
    professional: 297,
    enterprise: 697,
    minimumsAnnual: 1970,
    professionalAnnual: 2970,
    enterpriseAnnual: 6970,
  },
  
  // Credits (formatted with currency)
  credits: {
    minimums: isUK ? '£10' : '$10',
    professional: isUK ? '£25' : '$25',
    enterprise: isUK ? '£100' : '$100',
  },
  
  // Savings display
  savings: {
    monthly: isUK ? '£1,968' : '$1,968',
    competitorTotal: isUK ? '£2,165' : '$2,165',
    storilyPrice: isUK ? '£197' : '$197',
  }
} as const;

/**
 * Format a price with the region's currency symbol
 */
export const formatPrice = (amount: number): string => {
  return `${regionConfig.currency}${amount}`;
};

/**
 * Format a price per month
 */
export const formatPricePerMonth = (amount: number): string => {
  return `${regionConfig.currency}${amount}/mo`;
};

export default regionConfig;
